import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { ReportDefinition, ReportFolder, ReportStatus } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { NavIcon } from "../../components/NavIcon";
import { normalizeReportKey } from "../../utils/reportKey";

const STATUS_BADGE: Record<ReportStatus, string> = {
  DRAFT: "badge badge-draft",
  PUBLISHED: "badge badge-published",
  ARCHIVED: "badge badge-archived",
};

function relativeTime(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 30 ? `${days}d ago` : new Date(iso).toLocaleDateString();
}

/** One folder: its sub-folders and the reports inside it. Click a report to open it. */
export default function FolderView() {
  const { folderId = "" } = useParams();
  const navigate = useNavigate();
  const [folders, setFolders] = useState<ReportFolder[]>([]);
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKey, setNewKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([adminApi.folders.list(), adminApi.reports.list()])
      .then(([f, r]) => {
        setFolders(f);
        setReports(r);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load folder"))
      .finally(() => setLoading(false));
  }, [folderId]);

  const folder = folders.find((f) => f.id === folderId) ?? null;

  // Root -> ... -> this folder, for the breadcrumb.
  const trail = useMemo(() => {
    const chain: ReportFolder[] = [];
    let cur = folder;
    while (cur && chain.length < 12) {
      chain.unshift(cur);
      cur = cur.parentFolderId ? folders.find((f) => f.id === cur!.parentFolderId) ?? null : null;
    }
    return chain;
  }, [folder, folders]);

  const subfolders = folders.filter((f) => f.parentFolderId === folderId);
  const inFolder = reports.filter((r) => r.folderId === folderId);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inFolder;
    return inFolder.filter((r) => r.displayName.toLowerCase().includes(q) || r.reportKey.toLowerCase().includes(q));
  }, [inFolder, search]);
  const countIn = (id: string) => reports.filter((r) => r.folderId === id).length;

  const finalKey = normalizeReportKey(newKey);
  const keyTaken = !!finalKey && reports.some((r) => r.reportKey.toUpperCase() === finalKey);

  async function handleCreate() {
    if (!finalKey || !newName.trim() || keyTaken) return;
    setSubmitting(true);
    setCreateError(null);
    try {
      const created = await adminApi.reports.create({
        reportKey: finalKey,
        displayName: newName.trim(),
        folderId,
        status: "DRAFT",
        defaultFormat: "pdf",
        defaultPageLayout: "LANDSCAPE",
      });
      navigate(`/reports/${created.id}/designer`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create report");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p>Loading...</p>;

  if (!folder) {
    return (
      <div>
        <div className="page-header"><h1>Folder not found</h1></div>
        {error && <div className="error-text">{error}</div>}
        <p className="help-text">This folder doesn't exist or was deleted. <Link to="/folders">Back to folders</Link></p>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/folders">Folders</Link>
        {trail.map((f, i) => (
          <span key={f.id}>
            <span className="breadcrumb-sep">/</span>
            {i === trail.length - 1 ? <strong>{f.name}</strong> : <Link to={`/folders/${f.id}`}>{f.name}</Link>}
          </span>
        ))}
      </nav>

      <div className="page-header folder-view-header">
        <div className="folder-view-title">
          <div className="folder-row-icon folder-view-icon">
            <NavIcon name="folder" size={22} />
          </div>
          <div>
            <h1>
              {folder.name}
              {folder.shared && <span className="badge badge-published" style={{ marginLeft: 10, verticalAlign: "middle" }}>shared</span>}
            </h1>
            <div className="help-text">
              {folder.description ? `${folder.description} · ` : ""}
              {inFolder.length} report{inFolder.length === 1 ? "" : "s"}
              {subfolders.length > 0 ? ` · ${subfolders.length} sub-folder${subfolders.length === 1 ? "" : "s"}` : ""}
            </div>
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreating((v) => !v)}>
          + New report here
        </button>
      </div>

      {error && <div className="error-text">{error}</div>}

      {creating && (
        <div className="page-panel">
          <h3>New report in “{folder.name}”</h3>
          {createError && <div className="error-text">{createError}</div>}
          <div className="form-grid">
            <div className="form-row">
              <label>Display name</label>
              <input
                autoFocus
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (!keyTouched) setNewKey(normalizeReportKey(e.target.value, true));
                }}
                placeholder="Cashbook Payment"
              />
            </div>
            <div className="form-row">
              <label>Report key (unique, used in the API)</label>
              <input
                value={newKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setNewKey(normalizeReportKey(e.target.value, true));
                }}
                placeholder="CASHBOOK_PAYMENT"
                style={{ fontFamily: "var(--font-mono)" }}
              />
              {keyTaken ? (
                <span className="error-text">A report with key {finalKey} already exists — pick a different one.</span>
              ) : (
                <span className="help-text">Auto-formatted; can't be changed later.</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleCreate}
              disabled={submitting || keyTaken || !finalKey || !newName.trim()}
            >
              {submitting ? "Creating..." : "Create and open designer"}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {subfolders.length > 0 && (
        <div className="page-panel">
          <h3>Sub-folders</h3>
          <div className="subfolder-grid">
            {subfolders.map((f) => (
              <Link key={f.id} to={`/folders/${f.id}`} className="subfolder-card">
                <div className="folder-row-icon"><NavIcon name="folder" size={17} /></div>
                <div className="folder-row-main">
                  <div className="folder-row-name">{f.name}</div>
                  <div className="folder-row-desc">{countIn(f.id)} report{countIn(f.id) === 1 ? "" : "s"}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="page-panel">
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>Reports</h3>
          {inFolder.length > 4 && (
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports in this folder..."
              style={{ flex: "1 1 280px", marginLeft: 8 }}
            />
          )}
        </div>
        {inFolder.length === 0 ? (
          <div className="empty-state-icon" style={{ padding: 30 }}>
            <NavIcon name="reports" size={26} />
            <p>No reports in this folder yet.</p>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              Create the first report
            </button>
          </div>
        ) : (
          <DataTable
            rows={visible}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/reports/${r.id}/settings`)}
            emptyMessage={`No reports match "${search}".`}
            columns={[
              { key: "displayName", header: "Name" },
              { key: "reportKey", header: "Key", render: (r) => <code>{r.reportKey}</code> },
              { key: "status", header: "Status", render: (r) => <span className={STATUS_BADGE[r.status]}>{r.status}</span> },
              { key: "updatedAt", header: "Edited", render: (r) => <span className="help-text">{relativeTime(r.updatedAt)}</span> },
              {
                key: "actions",
                header: "",
                render: (r) => (
                  <div onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="btn-secondary" onClick={() => navigate(`/reports/${r.id}/designer`)}>
                      Open in Designer
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
